import React, { useState, useEffect, useReducer, useContext, useMemo, useRef } from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme as useThemeV4 } from "@material-ui/core/styles";
import {
  createTheme as createThemeV5,
  ThemeProvider as ThemeProviderV5,
} from "@mui/material/styles";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import {
  Avatar,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import AppTooltip from "../../components/AppTooltip";
import HubPageHeader from "../../components/HubPageHeader";
import MainContainer from "../../components/MainContainer";

import {
  Plus,
  Pencil,
  Trash2,
  Paperclip,
  MessageSquare,
  Globe,
  User as UserIcon,
  Inbox,
} from "lucide-react";

import api from "../../services/api";
import { getBackendUrl } from "../../config";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useSearch } from "../../context/Search/SearchContext";

const tLang = (key, fallback) => {
  const v = i18n.t(key);
  return !v || v === key ? fallback : v;
};

const hexToRgba = (hex, alpha = 1) => {
  if (!hex || typeof hex !== "string") return `rgba(25,118,210,${alpha})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return `rgba(25,118,210,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
};

const reducer = (state, action) => {
  if (action.type === "LOAD_QUICKMESSAGES") {
    const items = action.payload;
    const newItems = [];
    if (isArray(items)) {
      items.forEach((q) => {
        const idx = state.findIndex((u) => u.id === q.id);
        if (idx !== -1) state[idx] = q;
        else newItems.push(q);
      });
    }
    return [...state, ...newItems];
  }
  if (action.type === "UPDATE_QUICKMESSAGES") {
    const q = action.payload;
    const idx = state.findIndex((u) => u.id === q.id);
    if (idx !== -1) {
      state[idx] = q;
      return [...state];
    }
    return [q, ...state];
  }
  if (action.type === "DELETE_QUICKMESSAGE") {
    const id = action.payload;
    const idx = state.findIndex((u) => u.id === id);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
};

const useTableStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.palette.type === "dark" || theme.mode === "dark";
  return {
    excelTable: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0")}`,
        borderRight: "none",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        fontSize: 13.5,
        padding: "14px 16px",
      },
      "& .MuiTableCell-head": {
        background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        backgroundColor: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        fontWeight: 700,
        fontSize: 11.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        borderBottom: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
        padding: "12px 16px",
        whiteSpace: "nowrap",
        position: "sticky",
        top: 0,
        zIndex: 3,
      },
      "& .MuiTableCell-head:first-child": {
        borderTopLeftRadius: ds.radius?.md ?? 12,
      },
      "& .MuiTableCell-head:last-child": {
        borderTopRightRadius: ds.radius?.md ?? 12,
      },
      "& tbody tr:last-child .MuiTableCell-root": {
        borderBottom: "none",
      },
    },
    tableScroll: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      maxHeight: "100%",
      overflowX: "auto",
      overflowY: "auto",
      ...theme.scrollbarStyles,
    },
    zebraRow: {
      transition: "background-color 0.18s ease",
      "&:hover": {
        backgroundColor: dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
      },
    },
    actionsCell: {
      whiteSpace: "nowrap",
      width: 1,
    },
    iconActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
    },
    deleteActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: dsColors.danger || "#e53935",
      "&:hover": {
        background: dsColors.dangerBg || "rgba(229,57,53,0.10)",
        color: dsColors.dangerHover || "#c62828",
      },
    },
  };
});

const KpiPill = ({ icon, label, value, accent, isDark }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.25}
    sx={{
      px: 1.75,
      py: 1.25,
      borderRadius: 2.5,
      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
      flex: 1,
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${accent} 0%, ${hexToRgba(accent, 0.7)} 100%)`,
        color: "#fff",
        boxShadow: `0 4px 12px ${hexToRgba(accent, 0.3)}`,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "text.secondary", lineHeight: 1.2 }} noWrap>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: "text.primary", letterSpacing: -0.3, lineHeight: 1.2 }} noWrap>
        {value}
      </Typography>
    </Box>
  </Stack>
);

const ScopeBadge = ({ geral, isDark }) => {
  const cfg = geral
    ? { label: tLang("quickMessages.global", "Global"), color: "#2EA85A", icon: <Globe size={11} /> }
    : { label: tLang("quickMessages.personal", "Pessoal"), color: isDark ? "#8696a0" : "#667781", icon: <UserIcon size={11} /> };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.4,
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        background: hexToRgba(cfg.color, 0.15),
        color: cfg.color,
      }}
    >
      {cfg.icon}
      {cfg.label}
    </Box>
  );
};

const QuickMessages = () => {
  const tableClasses = useTableStyles();
  const themeV4 = useThemeV4();
  const isMobile = useMediaQuery(themeV4.breakpoints.down("sm"));

  const isDark = (themeV4?.palette?.type || themeV4?.mode) === "dark";
  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";
  const PRIMARY_CONTRAST = themeV4?.palette?.primary?.contrastText || "#fff";

  const tokens = useMemo(
    () => ({
      cardBg: isDark ? "#11141b" : "#ffffff",
      border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
      shadow: isDark
        ? "0 1px 0 rgba(255,255,255,0.02) inset, 0 8px 24px rgba(0,0,0,0.45)"
        : "0 1px 0 rgba(255,255,255,0.6) inset, 0 8px 24px rgba(15,23,42,0.06)",
      textPrimary: isDark ? "#e9edef" : "#0f172a",
      textSecondary: isDark ? "#8696a0" : "#64748b",
    }),
    [isDark]
  );

  const v5Theme = useMemo(
    () =>
      createThemeV5({
        palette: {
          mode: isDark ? "dark" : "light",
          primary: { main: PRIMARY_MAIN, dark: PRIMARY_DARK, contrastText: PRIMARY_CONTRAST },
          background: {
            default: isDark ? "#121212" : "#ffffff",
            paper: isDark ? "#0b0d12" : "#ffffff",
          },
          divider: isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)",
        },
        components: {
          MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 12 } } },
          MuiButton: { styleOverrides: { root: { borderRadius: 12 } } },
        },
      }),
    [isDark, PRIMARY_MAIN, PRIMARY_DARK, PRIMARY_CONTRAST]
  );

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedQuickemessage, setSelectedQuickemessage] = useState(null);
  const [deletingQuickemessage, setDeletingQuickemessage] = useState(null);
  const [quickemessageModalOpen, setQuickMessageDialogOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [visibilityFilter, setVisibilityFilter] = useState("all"); // all | mine | shared
  const [users, setUsers] = useState([]);
  const [quickemessages, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);
  const loadingMoreRef = useRef(false);

  // Carrega lista de usuários da empresa para mapear criadores das respostas rápidas
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users/list");
        setUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        // silencioso — fallback é mostrar só userId / inicial
      }
    })();
  }, []);

  const usersById = useMemo(() => {
    const map = {};
    users.forEach((u) => { map[u.id] = u; });
    return map;
  }, [users]);

  // Busca vem do header global (compartilhada via SearchContext)
  const { ticketSearchParam: searchParam, setTicketSearchParam, clear: clearSearch } = useSearch();

  // Limpa a busca global quando sai da página
  useEffect(() => () => clearSearch(), [clearSearch]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchQuickemessages();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const onQuickMessageEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUICKMESSAGES", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUICKMESSAGE", payload: +data.id });
      }
    };
    socket.on(`company-${companyId}-quickemessage`, onQuickMessageEvent);
    return () => {
      socket.off(`company-${companyId}-quickemessage`, onQuickMessageEvent);
    };
  }, [socket, user.companyId]);

  const fetchQuickemessages = async () => {
    try {
      const { data } = await api.get("/quick-messages", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_QUICKMESSAGES", payload: data.records });
      setHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
      loadingMoreRef.current = false;
    }
  };

  const handleOpenQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(true);
  };

  const handleCloseQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(false);
    fetchQuickemessages();
  };

  const handleEditQuickemessage = (q) => {
    setSelectedQuickemessage(q);
    setQuickMessageDialogOpen(true);
  };

  const handleDeleteQuickemessage = async (id) => {
    try {
      await api.delete(`/quick-messages/${id}`);
      toast.success(i18n.t("quickMessages.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingQuickemessage(null);
    setTicketSearchParam("");
    setPageNumber(1);
    fetchQuickemessages();
    dispatch({ type: "RESET" });
  };

  const loadMore = () => setPageNumber((p) => p + 1);
  const handleScroll = (e) => {
    if (!hasMore || loading || loadingMoreRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= 100) {
      loadingMoreRef.current = true;
      loadMore();
    }
  };

  const filteredQuickemessages = useMemo(() => {
    if (visibilityFilter === "mine") {
      return quickemessages.filter((q) => q.userId === user.id);
    }
    if (visibilityFilter === "shared") {
      return quickemessages.filter((q) => q.userId !== user.id);
    }
    return quickemessages;
  }, [quickemessages, visibilityFilter, user.id]);

  const stats = useMemo(() => {
    const total = filteredQuickemessages.length;
    const withMedia = filteredQuickemessages.filter((q) => q.mediaName).length;
    const global = filteredQuickemessages.filter((q) => q.geral === true).length;
    const personal = total - global;
    return { total, withMedia, global, personal };
  }, [filteredQuickemessages]);

  return (
    <ThemeProviderV5 theme={v5Theme}>
      <MainContainer>
        <Box
          sx={{
            background: isDark ? "#121212" : "#ffffff",
            height: "100%",
            width: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            paddingRight: "12px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ConfirmationModal
            title={deletingQuickemessage && `${i18n.t("quickMessages.confirmationModal.deleteTitle")} ${deletingQuickemessage.shortcode}?`}
            open={confirmModalOpen}
            onClose={setConfirmModalOpen}
            onConfirm={() => handleDeleteQuickemessage(deletingQuickemessage.id)}
          >
            {i18n.t("quickMessages.confirmationModal.deleteMessage")}
          </ConfirmationModal>
          <QuickMessageDialog
            resetPagination={() => {
              setPageNumber(1);
              fetchQuickemessages();
            }}
            open={quickemessageModalOpen}
            onClose={handleCloseQuickMessageDialog}
            aria-labelledby="form-dialog-title"
            quickemessageId={selectedQuickemessage && selectedQuickemessage.id}
          />

          <HubPageHeader
            icon={<MessageSquare size={20} strokeWidth={1.8} />}
            title={tLang("quickMessages.title", "Respostas rápidas")}
            subtitle={tLang("quickMessages.subtitle", "Atalhos para mensagens reutilizáveis no atendimento")}
            action={
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ pt: 0.75 }}>
              <FormControl size="small" variant="outlined" sx={{ minWidth: 200 }}>
                <InputLabel>{tLang("quickMessages.filters.visibility", "Visibilidade")}</InputLabel>
                <Select
                  value={visibilityFilter}
                  onChange={(e) => setVisibilityFilter(e.target.value)}
                  label={tLang("quickMessages.filters.visibility", "Visibilidade")}
                  sx={{
                    borderRadius: "12px",
                    "& .MuiOutlinedInput-notchedOutline": { borderRadius: "12px" },
                  }}
                  MenuProps={{
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    PaperProps: {
                      sx: {
                        borderRadius: "14px !important",
                        boxShadow: "0 10px 36px rgba(0,0,0,0.14) !important",
                        border: "none",
                        background: isDark ? "#121212 !important" : "#ffffff !important",
                        backgroundImage: "none !important",
                        mt: 1,
                        overflow: "hidden",
                        "& .MuiList-root": {
                          padding: "6px",
                        },
                        "& .MuiMenuItem-root": {
                          borderRadius: "8px",
                          padding: "9px 12px",
                          marginBottom: "4px",
                          fontSize: 14,
                          fontWeight: 500,
                          color: isDark ? "#ffffff" : "#1a1a1a",
                          "&:hover": {
                            background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                          },
                          "&.Mui-selected": {
                            background: "rgba(6, 207, 156, 0.10)",
                          },
                          "&.Mui-selected:hover": {
                            background: "rgba(6, 207, 156, 0.15)",
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="all">{tLang("quickMessages.filters.all", "Todas")}</MenuItem>
                  <MenuItem value="mine">{tLang("quickMessages.filters.mine", "Minhas")}</MenuItem>
                  <MenuItem value="shared">{tLang("quickMessages.filters.shared", "Compartilhadas")}</MenuItem>
                </Select>
              </FormControl>
              <Button
                onClick={handleOpenQuickMessageDialog}
                variant="contained"
                startIcon={<Plus size={16} />}
                sx={{
                  background: `linear-gradient(135deg, ${PRIMARY_MAIN} 0%, ${PRIMARY_DARK} 100%)`,
                  color: PRIMARY_CONTRAST,
                  borderRadius: "12px",
                  px: 2.5,
                  py: 1,
                  fontWeight: 700,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  boxShadow: `0 6px 16px ${hexToRgba(PRIMARY_MAIN, 0.35)}`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY_MAIN} 100%)`,
                    transform: "translateY(-1px)",
                    boxShadow: `0 10px 22px ${hexToRgba(PRIMARY_MAIN, 0.45)}`,
                  },
                }}
              >
                {i18n.t("quickMessages.buttons.add")}
              </Button>
              </Stack>
            }
          />

          {/* KPIs */}
          <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
            <Grid item xs={6} sm={3}>
              <KpiPill icon={<MessageSquare size={18} />} label={tLang("quickMessages.kpis.total", "Total")} value={stats.total} accent={PRIMARY_MAIN} isDark={isDark} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <KpiPill icon={<Paperclip size={18} />} label={tLang("quickMessages.kpis.withMedia", "Com mídia")} value={stats.withMedia} accent="#3b82f6" isDark={isDark} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <KpiPill icon={<Globe size={18} />} label={tLang("quickMessages.kpis.global", "Globais")} value={stats.global} accent="#2EA85A" isDark={isDark} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <KpiPill icon={<UserIcon size={18} />} label={tLang("quickMessages.kpis.personal", "Pessoais")} value={stats.personal} accent="#8b5cf6" isDark={isDark} />
            </Grid>
          </Grid>

          {/* TABELA / CARDS */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              borderRadius: 3,
              background: tokens.cardBg,
              border: tokens.border,
              boxShadow: tokens.shadow,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {isMobile ? (
              <Box
                onScroll={handleScroll}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  ...(themeV4.scrollbarStyles || {}),
                }}
              >
                {filteredQuickemessages.length === 0 && !loading ? (
                  <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ py: 6, px: 2 }}>
                    <Avatar
                      sx={{
                        width: 56, height: 56,
                        bgcolor: hexToRgba(PRIMARY_MAIN, isDark ? 0.15 : 0.10),
                        color: PRIMARY_MAIN,
                      }}
                    >
                      <Inbox size={28} />
                    </Avatar>
                    <Typography sx={{ fontSize: 13, color: tokens.textSecondary, textAlign: "center" }}>
                      {tLang("quickMessages.empty", "Nenhuma resposta rápida cadastrada.")}
                    </Typography>
                  </Stack>
                ) : (
                  filteredQuickemessages.map((q) => {
                    const creator = usersById[q.userId] || q.user;
                    return (
                    <Stack
                      key={q.id}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{
                        px: 1.75,
                        py: 1.5,
                        borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)"}`,
                        "&:last-child": { borderBottom: "none" },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: tokens.textPrimary }} noWrap>
                            {q.shortcode}
                          </Typography>
                          {q.mediaName && (
                            <Box sx={{ display: "inline-flex", color: tokens.textSecondary }}>
                              <Paperclip size={12} />
                            </Box>
                          )}
                          <ScopeBadge geral={q.geral} isDark={isDark} />
                        </Stack>
                        <Typography sx={{ fontSize: 12, color: tokens.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mt: 0.5 }}>
                          {q.message || tLang("quickMessages.table.emptyMessage", "—")}
                        </Typography>
                        {creator && (
                          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5 }}>
                            <Avatar
                              sx={{
                                width: 18, height: 18, fontSize: 9, fontWeight: 700,
                                bgcolor: hexToRgba(PRIMARY_MAIN, 0.2), color: PRIMARY_MAIN,
                              }}
                              src={
                                creator.profileImage
                                  ? (/^https?:\/\//i.test(creator.profileImage)
                                    ? creator.profileImage
                                    : `${getBackendUrl()}/public/avatar/${creator.profileImage}`)
                                  : undefined
                              }
                            >
                              {(creator.name || "?")[0].toUpperCase()}
                            </Avatar>
                            <Typography sx={{ fontSize: 11, color: tokens.textSecondary }} noWrap>
                              {creator.name}
                              {q.userId === user.id && ` (${tLang("quickMessages.table.you", "você")})`}
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                      <AppTooltip titleKey="quickMessages.tooltips.edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEditQuickemessage(q)}
                          className={tableClasses.iconActionBtn}
                        >
                          <Pencil size={16} strokeWidth={2} />
                        </IconButton>
                      </AppTooltip>
                      <AppTooltip titleKey="quickMessages.tooltips.delete">
                        <IconButton
                          size="small"
                          onClick={() => { setConfirmModalOpen(true); setDeletingQuickemessage(q); }}
                          className={tableClasses.deleteActionBtn}
                        >
                          <Trash2 size={16} strokeWidth={2} />
                        </IconButton>
                      </AppTooltip>
                    </Stack>
                    );
                  })
                )}
                {loading && <TableRowSkeleton columns={2} />}
              </Box>
            ) : (
              <TableContainer
                onScroll={handleScroll}
                className={tableClasses.tableScroll}
                style={{
                  flex: 1,
                  minHeight: 0,
                  height: "100%",
                  maxHeight: "100%",
                  overflow: "auto",
                }}
              >
                <Table stickyHeader className={tableClasses.excelTable} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{tLang("quickMessages.table.shortcode", "Atalho")}</TableCell>
                      <TableCell>{tLang("quickMessages.table.message", "Mensagem")}</TableCell>
                      <TableCell>{tLang("quickMessages.table.createdBy", "Criado por")}</TableCell>
                      <TableCell align="center">{tLang("quickMessages.table.status", "Escopo")}</TableCell>
                      <TableCell align="right" className={tableClasses.actionsCell}>{tLang("quickMessages.table.actions", "Ações")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredQuickemessages.length === 0 && !loading ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: "center", py: 6, color: tokens.textSecondary, fontStyle: "italic" }}>
                          {tLang("quickMessages.empty", "Nenhuma resposta rápida cadastrada.")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredQuickemessages.map((q) => {
                        const creator = usersById[q.userId] || q.user;
                        return (
                        <TableRow key={q.id} className={tableClasses.zebraRow}>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                              <Box component="span" sx={{ color: PRIMARY_MAIN, fontWeight: 700 }}>/</Box>
                              {q.shortcode}
                              {q.mediaName && (
                                <AppTooltip title={q.mediaName}>
                                  <Box component="span" sx={{ ml: 0.5, display: "inline-flex", color: tokens.textSecondary }}>
                                    <Paperclip size={12} />
                                  </Box>
                                </AppTooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 380 }}>
                            <AppTooltip
                              title={q.message || ""}
                              placement="top-start"
                            >
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  color: q.message ? tokens.textPrimary : tokens.textSecondary,
                                  fontStyle: q.message ? "normal" : "italic",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: 380,
                                }}
                              >
                                {q.message || tLang("quickMessages.table.emptyMessage", "—")}
                              </Typography>
                            </AppTooltip>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {creator ? (
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Avatar
                                  alt={creator.name}
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    bgcolor: hexToRgba(PRIMARY_MAIN, 0.2),
                                    color: PRIMARY_MAIN,
                                  }}
                                  src={
                                    creator.profileImage
                                      ? (/^https?:\/\//i.test(creator.profileImage)
                                        ? creator.profileImage
                                        : `${getBackendUrl()}/public/avatar/${creator.profileImage}`)
                                      : undefined
                                  }
                                >
                                  {(creator.name || "?")[0].toUpperCase()}
                                </Avatar>
                                <Typography sx={{ fontSize: 13, color: tokens.textPrimary }}>
                                  {creator.name}
                                  {q.userId === user.id && (
                                    <Box component="span" sx={{ ml: 0.5, fontSize: 11, color: tokens.textSecondary, fontStyle: "italic" }}>
                                      ({tLang("quickMessages.table.you", "você")})
                                    </Box>
                                  )}
                                </Typography>
                              </Stack>
                            ) : (
                              <Typography sx={{ fontSize: 12, color: tokens.textSecondary, fontStyle: "italic" }}>—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <ScopeBadge geral={q.geral} isDark={isDark} />
                          </TableCell>
                          <TableCell align="right" className={tableClasses.actionsCell}>
                            <AppTooltip titleKey="quickMessages.tooltips.edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditQuickemessage(q)}
                                className={tableClasses.iconActionBtn}
                              >
                                <Pencil size={16} strokeWidth={2} />
                              </IconButton>
                            </AppTooltip>
                            <AppTooltip titleKey="quickMessages.tooltips.delete">
                              <IconButton
                                size="small"
                                onClick={() => { setConfirmModalOpen(true); setDeletingQuickemessage(q); }}
                                className={tableClasses.deleteActionBtn}
                              >
                                <Trash2 size={16} strokeWidth={2} />
                              </IconButton>
                            </AppTooltip>
                          </TableCell>
                        </TableRow>
                        );
                      })
                    )}
                    {loading && <TableRowSkeleton columns={5} />}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
        </Box>
      </MainContainer>
    </ThemeProviderV5>
  );
};

export default QuickMessages;
